/**
 * SupplierFiscalConfig — Modal/Drawer for supplier fiscal configuration
 * 
 * Phase 1: Explicit Configuration
 * - Regime IVA: Dynamic from SAGE (select)
 * - Taxa IVA: Based on selected regime TX1-TX4 (select)
 * - Motivo Isenção: Dynamic from SAGE (select, only when rate=0)
 * - Notas: Free text
 * 
 * Design: 
 * - NO inference of fiscal rules
 * - NO TipoIVACompra → TX mapping
 * - All values explicitly selected by user
 */

import React, { useState, useEffect, useCallback } from 'react';
import { X, AlertCircle, Check, RefreshCw, Info } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fiscal status badges
const StatusBadge = ({ status }) => {
  const config = {
    valid: { color: 'bg-green-100 text-green-800 border-green-200', icon: '🟢', label: 'Configurado' },
    incomplete: { color: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: '🟡', label: 'Incompleto' },
    not_configured: { color: 'bg-red-100 text-red-800 border-red-200', icon: '🔴', label: 'Não Configurado' },
  };
  
  const { color, icon, label } = config[status] || config.not_configured;
  
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${color}`}>
      <span className="mr-1">{icon}</span>
      {label}
    </span>
  );
};

const SupplierFiscalConfig = ({ 
  isOpen, 
  onClose, 
  supplierCode, 
  supplierName,
  onSave,
  token 
}) => {
  // State
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  
  // Fiscal data from SAGE (cached)
  const [regimes, setRegimes] = useState([]);
  const [motivos, setMotivos] = useState([]);
  const [regimesLoading, setRegimesLoading] = useState(false);
  const [motivosLoading, setMotivosLoading] = useState(false);
  
  // Current config
  const [config, setConfig] = useState({
    purchase_vat_regime: null,
    purchase_vat_rate: null,
    purchase_exemption_code: null,
    purchase_vat_notes: '',
  });
  const [fiscalStatus, setFiscalStatus] = useState('not_configured');
  const [validation, setValidation] = useState({});
  const [lastUpdated, setLastUpdated] = useState(null);
  const [lastUpdatedBy, setLastUpdatedBy] = useState(null);
  
  // Derived: available rates based on selected regime
  const availableRates = React.useMemo(() => {
    if (!config.purchase_vat_regime || !regimes.length) return [];
    const regime = regimes.find(r => r.codigo === config.purchase_vat_regime);
    return regime?.taxas_disponiveis || [];
  }, [config.purchase_vat_regime, regimes]);
  
  // Load SAGE tax tables
  const loadTaxTables = useCallback(async (forceRefresh = false) => {
    setRegimesLoading(true);
    setMotivosLoading(true);
    
    try {
      const refreshParam = forceRefresh ? '?refresh=true' : '';
      
      const [regimesRes, motivosRes] = await Promise.all([
        fetch(`${API_URL}/api/sage/tax-tables/regimes${refreshParam}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/sage/tax-tables/motivos-isencao${refreshParam}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      const regimesData = await regimesRes.json();
      const motivosData = await motivosRes.json();
      
      if (regimesData.success) {
        setRegimes(regimesData.regimes || []);
      } else if (regimesData.error) {
        console.warn('Regimes load warning:', regimesData.error);
      }
      
      if (motivosData.success) {
        setMotivos(motivosData.motivos || []);
      } else if (motivosData.error) {
        console.warn('Motivos load warning:', motivosData.error);
      }
    } catch (err) {
      console.error('Error loading tax tables:', err);
    } finally {
      setRegimesLoading(false);
      setMotivosLoading(false);
    }
  }, [token]);
  
  // Load supplier fiscal config
  const loadConfig = useCallback(async () => {
    if (!supplierCode) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const res = await fetch(`${API_URL}/api/suppliers/${supplierCode}/fiscal`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!res.ok) throw new Error('Erro ao carregar configuração');
      
      const data = await res.json();
      
      setConfig({
        purchase_vat_regime: data.purchase_vat_regime || null,
        purchase_vat_rate: data.purchase_vat_rate,
        purchase_exemption_code: data.purchase_exemption_code || null,
        purchase_vat_notes: data.purchase_vat_notes || '',
      });
      setFiscalStatus(data.fiscal_status);
      setValidation(data.fiscal_validation || {});
      setLastUpdated(data.fiscal_config_updated_at);
      setLastUpdatedBy(data.fiscal_config_updated_by);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [supplierCode, token]);
  
  // Initial load
  useEffect(() => {
    if (isOpen && supplierCode) {
      loadTaxTables();
      loadConfig();
    }
  }, [isOpen, supplierCode, loadTaxTables, loadConfig]);
  
  // Handle regime change - reset rate if not available in new regime
  const handleRegimeChange = (newRegime) => {
    setConfig(prev => {
      const regime = regimes.find(r => r.codigo === newRegime);
      const availableRates = regime?.taxas_disponiveis || [];
      
      // Reset rate if current rate not available in new regime
      const newRate = availableRates.includes(prev.purchase_vat_rate) 
        ? prev.purchase_vat_rate 
        : null;
      
      return {
        ...prev,
        purchase_vat_regime: newRegime || null,
        purchase_vat_rate: newRate,
        // Clear exemption if rate is no longer 0
        purchase_exemption_code: newRate === 0 ? prev.purchase_exemption_code : null,
      };
    });
  };
  
  // Handle rate change
  const handleRateChange = (newRate) => {
    const rateValue = newRate === '' ? null : parseFloat(newRate);
    setConfig(prev => ({
      ...prev,
      purchase_vat_rate: rateValue,
      // Clear exemption code if rate > 0
      purchase_exemption_code: rateValue === 0 ? prev.purchase_exemption_code : null,
    }));
  };
  
  // Save configuration
  const handleSave = async () => {
    setSaving(true);
    setError(null);
    
    try {
      const res = await fetch(`${API_URL}/api/suppliers/${supplierCode}/fiscal`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(config),
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Erro ao guardar');
      }
      
      const data = await res.json();
      setFiscalStatus(data.fiscal_status);
      setValidation(data.fiscal_validation || {});
      setLastUpdated(data.fiscal_config_updated_at);
      setLastUpdatedBy(data.fiscal_config_updated_by);
      
      // Notify parent
      if (onSave) {
        onSave(data);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div 
          className="relative bg-white rounded-lg shadow-xl max-w-lg w-full"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Configuração Fiscal de Compras
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                {supplierCode} — {supplierName}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          {/* Content */}
          <div className="px-6 py-4 space-y-4">
            {/* Status Badge */}
            <div className="flex items-center justify-between">
              <StatusBadge status={fiscalStatus} />
              {lastUpdated && (
                <span className="text-xs text-gray-400">
                  Última alteração: {new Date(lastUpdated).toLocaleString('pt-PT')}
                  {lastUpdatedBy && ` por ${lastUpdatedBy}`}
                </span>
              )}
            </div>
            
            {/* Error */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3 flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}
            
            {/* Validation warnings */}
            {validation.warnings?.length > 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                <div className="flex items-start gap-2">
                  <Info className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-yellow-700">
                    {validation.warnings.map((w, i) => (
                      <p key={i}>{w}</p>
                    ))}
                  </div>
                </div>
              </div>
            )}
            
            {loading ? (
              <div className="py-8 text-center text-gray-500">
                <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                A carregar configuração...
              </div>
            ) : (
              <div className="space-y-4">
                {/* Regime IVA */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Regime IVA *
                  </label>
                  <select
                    value={config.purchase_vat_regime || ''}
                    onChange={e => handleRegimeChange(e.target.value)}
                    disabled={regimesLoading}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    data-testid="fiscal-regime-select"
                  >
                    <option value="">Selecione o regime...</option>
                    {regimes.map(r => (
                      <option key={r.codigo} value={r.codigo}>
                        {r.codigo} — {r.descricao}
                      </option>
                    ))}
                  </select>
                  {regimesLoading && (
                    <p className="text-xs text-gray-400 mt-1">A carregar do SAGE...</p>
                  )}
                </div>
                
                {/* Taxa IVA */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Taxa IVA *
                  </label>
                  <select
                    value={config.purchase_vat_rate ?? ''}
                    onChange={e => handleRateChange(e.target.value)}
                    disabled={!config.purchase_vat_regime || availableRates.length === 0}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                    data-testid="fiscal-rate-select"
                  >
                    <option value="">Selecione a taxa...</option>
                    {availableRates.map(rate => (
                      <option key={rate} value={rate}>
                        {rate}%
                      </option>
                    ))}
                  </select>
                  {!config.purchase_vat_regime && (
                    <p className="text-xs text-gray-400 mt-1">
                      Selecione primeiro o Regime IVA
                    </p>
                  )}
                  {config.purchase_vat_regime && availableRates.length > 0 && (
                    <p className="text-xs text-gray-400 mt-1">
                      Taxas disponíveis para {config.purchase_vat_regime}: {availableRates.map(r => `${r}%`).join(', ')}
                    </p>
                  )}
                </div>
                
                {/* Motivo de Isenção (only when rate = 0) */}
                {config.purchase_vat_rate === 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Motivo de Isenção *
                    </label>
                    <select
                      value={config.purchase_exemption_code || ''}
                      onChange={e => setConfig(prev => ({ 
                        ...prev, 
                        purchase_exemption_code: e.target.value || null 
                      }))}
                      disabled={motivosLoading}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      data-testid="fiscal-exemption-select"
                    >
                      <option value="">Selecione o motivo...</option>
                      {motivos.map(m => (
                        <option key={m.codigo} value={m.codigo}>
                          {m.codigo} — {m.descricao}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-amber-600 mt-1">
                      Obrigatório quando Taxa IVA = 0%
                    </p>
                  </div>
                )}
                
                {/* Notas */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notas
                  </label>
                  <textarea
                    value={config.purchase_vat_notes || ''}
                    onChange={e => setConfig(prev => ({ 
                      ...prev, 
                      purchase_vat_notes: e.target.value 
                    }))}
                    rows={2}
                    placeholder="Notas adicionais sobre a configuração fiscal..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    data-testid="fiscal-notes-input"
                  />
                </div>
                
                {/* Validation message */}
                {validation.message && !validation.can_send_to_sage && (
                  <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                    <p className="text-sm text-amber-700">{validation.message}</p>
                    {validation.missing_fields?.length > 0 && (
                      <p className="text-xs text-amber-600 mt-1">
                        Campos em falta: {validation.missing_fields.join(', ')}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
          
          {/* Footer */}
          <div className="flex items-center justify-between px-6 py-4 border-t bg-gray-50 rounded-b-lg">
            <button
              onClick={() => loadTaxTables(true)}
              disabled={regimesLoading || motivosLoading}
              className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1"
            >
              <RefreshCw className={`w-4 h-4 ${(regimesLoading || motivosLoading) ? 'animate-spin' : ''}`} />
              Atualizar listas SAGE
            </button>
            
            <div className="flex gap-2">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={saving || loading}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                data-testid="fiscal-save-button"
              >
                {saving ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Check className="w-4 h-4" />
                )}
                Guardar
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export { SupplierFiscalConfig, StatusBadge };
export default SupplierFiscalConfig;
